import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';

// Renders the inspector's pre-inspection agreement body as markdown.
//
// Shared by the public signing page and the Settings preview so both surfaces
// format identically. The project has no @tailwindcss/typography plugin and
// Tailwind's preflight strips default heading/list styling, so every element
// is styled explicitly with utilities here.
//
// `remarkBreaks` makes single newlines render as <br>. Older agreements were
// authored as plain text with hard line breaks (addresses, signature blocks);
// without this they would collapse into run-on paragraphs.

const components: Components = {
  h1: ({ children }) => (
    <h1 className="text-lg font-semibold text-slate-900 mt-5 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base font-semibold text-slate-900 mt-5 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-900 mt-4 mb-1.5 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-slate-700 mt-4 mb-1.5 first:mt-0">{children}</h4>
  ),
  p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-slate-200 pl-3 text-slate-600 mb-3">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="font-mono text-xs bg-slate-100 rounded px-1 py-0.5">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="font-mono text-xs bg-slate-100 rounded p-3 mb-3 overflow-x-auto">{children}</pre>
  ),
  hr: () => <hr className="border-slate-200 my-4" />,
  table: ({ children }) => (
    <table className="border-collapse mb-3 text-sm">{children}</table>
  ),
  th: ({ children }) => (
    <th className="border border-slate-200 px-2 py-1 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-slate-200 px-2 py-1">{children}</td>,
};

interface AgreementMarkdownProps {
  children: string;
}

export function AgreementMarkdown({ children }: AgreementMarkdownProps) {
  return (
    <ReactMarkdown remarkPlugins={[remarkBreaks]} components={components}>
      {children}
    </ReactMarkdown>
  );
}
